import React, { useState, useEffect } from 'react'
import { useStoreState } from 'easy-peasy'
import {
  Box,
  useRadio,
  useRadioGroup,
  VStack,
  Heading,
  Text,
  Button,
  Select
} from '@chakra-ui/react'
import {
  NoHeadsetIcon,
  Headphones,
  InPersonFilled,
  Online
} from '@blueprinthq/joy'
import {
  DialogContainer,
  DialogActions,
  DialogHeader,
  DialogBody
} from '@handlers/sessions/components/dialog'
import { ReturnUserStartRecordingDialog } from './return-user-start-recording-dialog'
import { useExperienceManager, useAudioCapture } from '@hooks'
import { Mic, MicOff } from '@components/icons'
import ReportProblem from '@material-ui/icons/ReportProblem'
import { isMobile } from 'react-device-detect'
import { trackEvent } from '@lib/clinician-tracking'
import flagsmith from 'flagsmith'
import { FlagsmithFeatures } from '@constants/flagsmith'

type StartRecordingDialogPropsT = {
  isOpen: boolean
  onClose: () => void
  startRecording: () => void
  onShareAudio: () => Promise<void>
  hasClinicianOnboardedToScribe: boolean
}

function ModeOption(props: any) {
  const { getInputProps, getCheckboxProps } = useRadio(props)

  const input = getInputProps()
  const checkbox = getCheckboxProps()

  return (
    <Box as="label" w="100%" cursor="pointer" m={0} p={0}>
      <input {...input} />
      <Box
        {...checkbox}
        py={3}
        px={4}
        borderRadius="24px"
        border="1px solid #E4E5E6"
        _checked={{
          bg: 'rgba(45, 84, 232, 0.04)',
          border: '2px #2D54E8 solid'
        }}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {props.children}
      </Box>
    </Box>
  )
}

const EnableYourMicrophone = ({
  onClose,
  onEnable
}: {
  onClose: () => void
  onEnable: () => void
}) => {
  useEffect(() => {
    trackEvent('Start Recording Dialog -> Viewed Enable Microphone')
  }, [])

  return (
    <>
      <DialogActions onClose={onClose} />
      <DialogBody>
        <VStack spacing={6}>
          <Box>
            <MicOff width="80px" height="80px" />
          </Box>
          <Box textAlign="center">
            <DialogHeader text="Enable your microphone" />
            <Text>
              Allow your browser to use your microphone so we can record audio.
            </Text>
          </Box>
          <Button onClick={onEnable} m={0} isFullWidth>
            Enable microphone
          </Button>
        </VStack>
      </DialogBody>
    </>
  )
}

const SelectMicrophone = ({
  onClose,
  onNext
}: {
  onClose: () => void
  onNext: () => void
}) => {
  const {
    audioInputs,
    selectedAudioInput,
    setSelectedAudioInput
  } = useAudioCapture()
  const { audioLevel } = useStoreState(state => state.volume)

  useEffect(() => {
    trackEvent('Start Recording Dialog -> Viewed Microphone Selection')
  }, [])

  return (
    <>
      <DialogActions onClose={onClose} />
      <DialogBody>
        <VStack spacing={6}>
          <Box>
            <Mic
              dy={1 - audioLevel}
              width="80px"
              height="80px"
              filterId="startRecording"
            />
          </Box>
          <Box textAlign="center">
            <DialogHeader text="Select a microphone" />
            <Text>
              The icon above should light up green when you speak out loud.
            </Text>
          </Box>
          <Box w="100%">
            <Select
              onChange={e => setSelectedAudioInput(e.target.value)}
              // @ts-ignore
              value={selectedAudioInput}
              border="1px solid"
              height="40px"
              borderColor="pale_gray"
              _focus={{ outline: 'none' }}
            >
              {audioInputs.map(input => (
                <option key={input.deviceId} value={input.deviceId}>
                  {input.label}
                </option>
              ))}
            </Select>
          </Box>
          <Button isFullWidth onClick={onNext}>
            Next
          </Button>
        </VStack>
      </DialogBody>
    </>
  )
}

const SelectSetting = ({
  isTelehealth,
  onClose,
  onChange,
  onNext,
  onBack,
  startRecording
}: {
  isTelehealth: boolean
  onClose: () => void
  onChange: (mode: string) => void
  onNext: () => void
  onBack: () => void
  startRecording: () => void
}) => {
  useEffect(() => {
    trackEvent('Start Recording Dialog -> Viewed Setting Selection')
  }, [])

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'framework',
    defaultValue: 'react',
    onChange: value => {
      onChange(value)
    }
  })
  const group = getRootProps()

  const showStartRecording = isMobile || !isTelehealth

  return (
    <>
      <DialogActions onBack={onBack} />
      <DialogBody>
        <VStack spacing={6}>
          <Box textAlign="center">
            <DialogHeader text="Is this session happening In-Person or via Telehealth?" />
            <Text>We optimize audio settings to improve note quality.</Text>
          </Box>
          <VStack {...group} spacing={4} w="100%">
            <ModeOption
              key="in_person"
              {...getRadioProps({ value: 'in_person' })}
              isChecked={!isTelehealth}
            >
              <InPersonFilled />
              <Box ml={3}>In-Person</Box>
            </ModeOption>
            <ModeOption
              key="telehealth"
              {...getRadioProps({ value: 'telehealth' })}
              isChecked={isTelehealth}
            >
              <Online />
              <Box ml={3}>Telehealth</Box>
            </ModeOption>
          </VStack>
          {showStartRecording ? (
            <Button onClick={startRecording} m={0} isFullWidth>
              Start recording
            </Button>
          ) : (
            <Button onClick={onNext} m={0} isFullWidth>
              Next
            </Button>
          )}
        </VStack>
      </DialogBody>
    </>
  )
}

const SelectUsingHeadphones = ({
  isUsingHeadphones,
  onClose,
  onChange,
  onNext,
  onBack,
  showSafariWarning
}: {
  isUsingHeadphones: boolean
  onClose: () => void
  onChange: (mode: string) => void
  onNext: () => void
  onBack: () => void
  showSafariWarning: boolean
}) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'framework',
    defaultValue: 'react',
    onChange: value => {
      onChange(value)
    }
  })
  const group = getRootProps()

  return (
    <>
      <DialogActions onBack={onBack} />
      <DialogBody>
        {showSafariWarning ? (
          <VStack spacing={6}>
            <Box>
              <ReportProblem style={{ height: 80, width: 80 }} />
            </Box>
            <Box textAlign="center">
              <DialogHeader text="Audio recording for telehealth sessions is not fully supported in Safari browser" />
              <Text>
                Due to a limitation of the Safari browser, the AI Notetaker
                feature will not be able to capture participant audio duing a
                telehealth session.
              </Text>
              <Text mt={6}>For the best results, please use Chrome.</Text>
            </Box>
            <Button onClick={onNext} m={0} isFullWidth>
              Got it
            </Button>
          </VStack>
        ) : (
          <VStack spacing={6}>
            <Box>
              <Box textAlign="center">
                <Heading as="h3" fontSize="24px">
                  Are you planning on using headphones?
                </Heading>
                <Text>
                  If you are planning on using headphones, there is one more
                  important setting you should consider.
                </Text>
              </Box>
            </Box>
            <VStack {...group} spacing={4} w="100%">
              <ModeOption
                key="using_headphones"
                {...getRadioProps({ value: 'using_headphones' })}
                isChecked={isUsingHeadphones}
              >
                <Headphones />
                <Box ml={3}>Using headphones</Box>
              </ModeOption>
              <ModeOption
                key="telehealth"
                {...getRadioProps({ value: 'no_headphones' })}
                isChecked={!isUsingHeadphones}
              >
                <NoHeadsetIcon />
                <Box ml={3}>No headphones</Box>
              </ModeOption>
            </VStack>
            <Button onClick={onNext} m={0} isFullWidth>
              {!isUsingHeadphones ? 'Start recording' : 'Next'}
            </Button>
          </VStack>
        )}
      </DialogBody>
    </>
  )
}

const ShareAudio = ({
  startRecording,
  onBack,
  handleShareAudio
}: {
  onClose: () => void
  startRecording: () => void
  onBack: () => void
  handleShareAudio: () => Promise<void>
}) => {
  return (
    <>
      <DialogActions onBack={onBack} />
      <DialogBody>
        <Box w="100%">
          <Box textAlign="center" mb={6}>
            <DialogHeader text="Share audio from another browser tab" />
            <Text>
              Select the tab in your browser that you’ll be running your
              telehealth session if you want the AI Notetaker to capture what
              your client is saying.
            </Text>
          </Box>
          <Box w="100%">
            <Button w="100%" onClick={handleShareAudio} m={0} mb={4}>
              Share browser audio
            </Button>
            <Button w="100%" variant="outline" onClick={startRecording} m={0}>
              Skip and start recording
            </Button>
          </Box>
        </Box>
      </DialogBody>
    </>
  )
}

const StartRecordingDialog = ({
  isOpen,
  onClose,
  onShareAudio,
  startRecording,
  hasClinicianOnboardedToScribe
}: StartRecordingDialogPropsT) => {
  const [step, setStep] = useState('enable_microphone')
  const [isMicEnabled, setIsMicEnabled] = useState(false)

  const showReturnUserStartRecordingDialog = flagsmith.hasFeature(
    FlagsmithFeatures.REDUCED_CLICKS_START_RECORDING_FLOW
  )

  const {
    isReady,
    audioInputs,
    promptForDevicePermissions,
    isTelehealth,
    setIsTelehealth,
    isUsingHeadphones,
    setIsUsingHeadphones
  } = useAudioCapture()
  const { isSafari, isExtension } = useExperienceManager()

  useEffect(() => {
    if (audioInputs.some(d => d.deviceId)) {
      setStep('select_microphone')
      setIsMicEnabled(true)
    }
  }, [audioInputs])

  const handleShareAudio = async () => {
    try {
      await onShareAudio()
      startRecording()
    } catch (err) {
      console.log({ err })
    }
  }

  const handleHeadphoneSelection = () => {
    if (isUsingHeadphones) {
      setStep('share_audio')
    } else {
      startRecording()
    }
  }

  const handleSelectSetting = (setting: string) => {
    setIsTelehealth(setting === 'telehealth')
  }

  const handleSelectUsingHeadphones = (val: string) => {
    setIsUsingHeadphones(val === 'using_headphones')
  }

  const renderStep = () => {
    if (step === 'enable_microphone') {
      return (
        <EnableYourMicrophone
          onClose={onClose}
          onEnable={promptForDevicePermissions}
        />
      )
    } else if (step === 'select_microphone') {
      return (
        <SelectMicrophone
          onNext={() => setStep('select_setting')}
          onClose={onClose}
        />
      )
    } else if (step === 'select_setting') {
      return (
        <SelectSetting
          isTelehealth={isTelehealth}
          onClose={onClose}
          onChange={handleSelectSetting}
          startRecording={startRecording}
          onNext={
            !isMobile
              ? () => setStep('select_using_headphones')
              : startRecording
          }
          onBack={() => setStep('select_microphone')}
        />
      )
    } else if (step === 'select_using_headphones') {
      const showSafariWarning = isSafari && !isMobile
      return (
        <SelectUsingHeadphones
          isUsingHeadphones={isUsingHeadphones}
          onClose={onClose}
          onChange={val => handleSelectUsingHeadphones(val)}
          onNext={showSafariWarning ? startRecording : handleHeadphoneSelection}
          onBack={() => setStep('select_setting')}
          showSafariWarning={showSafariWarning}
        />
      )
    } else if (step === 'share_audio') {
      return (
        <ShareAudio
          onClose={onClose}
          startRecording={startRecording}
          handleShareAudio={handleShareAudio}
          onBack={() => setStep('select_using_headphones')}
        />
      )
    }
  }

  return isReady ? (
    <DialogContainer
      isOpen={isOpen}
      onClose={onClose}
      isExtension={isExtension}
    >
      {showReturnUserStartRecordingDialog && hasClinicianOnboardedToScribe && isMicEnabled ? (
        <ReturnUserStartRecordingDialog
          isExtension={isExtension}
          onClose={onClose}
          handleShareAudio={handleShareAudio}
          startRecording={startRecording}
        />
      ) : (
        renderStep()
      )}
    </DialogContainer>
  ) : null
}

export default StartRecordingDialog
